import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Star, BookOpen, ArrowRight } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface ReviewTile {
  path: string;
  name: string;
  rating: number;
  summary: string;
}

const REVIEW_POOL: ReviewTile[] = [
  {
    path: "/rezension/immozentrale-eu",
    name: "Immozentrale.eu",
    rating: 4.2,
    summary: "Spezialplattform für Gewerbeimmobilien in DACH – solide Filter, transparent bei Nebenkosten.",
  },
  {
    path: "/rezension/frank-hoffmann-immobilien",
    name: "Frank Hoffmann Immobilien",
    rating: 4.5,
    summary: "Regionaler Makler mit starkem Fokus auf Norddeutschland und persönlicher Beratung.",
  },
  {
    path: "/rezension/realogis-de",
    name: "Realogis",
    rating: 4.4,
    summary: "Marktführer für Industrie- und Logistikimmobilien mit sehr aktuellen Listings.",
  },
  {
    path: "/rezension/immoscout24-review",
    name: "ImmoScout24 Gewerbe",
    rating: 4.0,
    summary: "Größte Reichweite, gute Filter – aber viele Anbieter zeigen Preise erst nach Kontaktaufnahme.",
  },
];

const ARTICLE_FALLBACK: { path: string; title: string; excerpt: string }[] = [
  {
    path: "/ressourcen/gewerbeimmobilien-trends",
    title: "Gewerbeimmobilien Trends 2026",
    excerpt: "Die wichtigsten Entwicklungen im deutschen Gewerbemarkt – ESG, Leerstände und Preisdynamik.",
  },
  {
    path: "/ressourcen/mietpreis-gewerbe-berechnen",
    title: "Mietpreis Gewerbe berechnen",
    excerpt: "So kalkulierst du 2026 die faire Miete für deine Gewerbefläche – mit Marktdaten aus 420 Städten.",
  },
  {
    path: "/ressourcen/lagerhalle-mieten-kosten",
    title: "Lagerhalle mieten – Kosten 2026",
    excerpt: "Preise, Nebenkosten und versteckte Aufwände beim Anmieten einer Lagerhalle in Deutschland.",
  },
];

function pickDeterministic<T>(items: T[], seed: string): T {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) | 0;
  return items[Math.abs(hash) % items.length];
}

interface Props {
  currentPath: string;
}

export const RelatedTilesPair = ({ currentPath }: Props) => {
  const review = pickDeterministic(REVIEW_POOL, currentPath);
  const [article, setArticle] = useState(pickDeterministic(ARTICLE_FALLBACK, currentPath));

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await (supabase as any)
          .from("published_articles")
          .select("slug,title,meta_description")
          .eq("status", "published")
          .order("created_at", { ascending: false })
          .limit(6);
        if (cancelled || !data?.length) return;
        const picked = pickDeterministic(data, currentPath) as any;
        setArticle({
          path: `/${picked.slug}`,
          title: picked.title,
          excerpt: picked.meta_description || "Weiterlesen für aktuelle Zahlen und Praxisbeispiele.",
        });
      } catch {
        // silently keep fallback
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentPath]);

  return (
    <section
      aria-label="Passende Bewertung und Artikel"
      className="my-10 grid grid-cols-1 md:grid-cols-2 gap-4"
    >
      <Link
        to={review.path}
        className="group rounded-xl border border-border bg-card p-5 hover:border-primary transition-colors"
      >
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs font-semibold uppercase tracking-wider text-primary">
            Bewertung
          </span>
          <span className="flex items-center gap-1 text-sm text-foreground">
            <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" aria-hidden="true" />
            {review.rating.toFixed(1)}
          </span>
        </div>
        <h3 className="text-lg font-semibold text-foreground mb-1 group-hover:text-primary">
          {review.name}
        </h3>
        <p className="text-sm text-muted-foreground mb-3">{review.summary}</p>
        <span className="inline-flex items-center gap-1 text-sm text-primary font-medium">
          Bewertung lesen <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </span>
      </Link>

      <Link
        to={article.path}
        className="group rounded-xl border border-border bg-card p-5 hover:border-primary transition-colors"
      >
        <div className="flex items-center gap-2 mb-2">
          <BookOpen className="h-4 w-4 text-primary" aria-hidden="true" />
          <span className="text-xs font-semibold uppercase tracking-wider text-primary">
            Ratgeber
          </span>
        </div>
        <h3 className="text-lg font-semibold text-foreground mb-1 group-hover:text-primary">
          {article.title}
        </h3>
        <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{article.excerpt}</p>
        <span className="inline-flex items-center gap-1 text-sm text-primary font-medium">
          Artikel öffnen <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </span>
      </Link>
    </section>
  );
};
